<template>
  <div class="contractor-form" :class="{ history: isHistoryRecord }">
    <form v-scroll-spy="{ allowNoActive: true }" class="vertical" data-scroll-spy-id="sub-tab">
      <section v-if="canViewUserAccount">
        <h1>{{ $t('contractor.labels.userAccount') }}</h1>
        <div>
          <router-link :to="`/users/${formData.user.reference_number}`"
            ><font-awesome-icon icon="user"></font-awesome-icon>
            {{ formData.user.username }}</router-link
          >
          <span v-if="!formData.user.is_active" class="inactive">
            {{ $t('users.labels.inactiveUser') }}
          </span>
        </div>
      </section>

      <section>
        <h1>{{ $t('contractor.labels.personalDetails') }}</h1>

        <div class="payroll-entity-name-container">
          <div class="entity-and-payroll-row">
            <div class="form-control">
              <label>{{ $t('contractor.labels.payrollNumber') }}</label>
              <InputField
                v-model="currentPayroll"
                type="text"
                class="bordered"
                field-icon="barcode"
                picker="plus"
                @change="onChange"
                @picker-click="addPayrollNum(currentPayroll)"
              />
              <tol-list :list-items="formData.payroll_nums">
                <template v-slot:body="{ value }">
                  <div>
                    <span>{{ value }}</span>
                    <span class="list-action" @click="removePayrollNum(value)">
                      <font-awesome-icon icon="minus-circle"></font-awesome-icon>
                    </span>
                  </div>
                </template>
              </tol-list>
            </div>

            <div class="form-control">
              <label>{{ $t('contractor.labels.entityType') }}<span class="asterisk">*</span></label>
              <DropdownSelect
                :menu-items="entityTypes"
                :selected-item="camelCaseOptions(formData.entity)"
                class="blue bordered"
                :class="{ error: formErrors['Entity'] }"
                @select-item="onEntityTypeChange"
              />
              <div
                v-if="
                  (isEffectiveDateEnabled || formData.effective_date) && formData.reference_number
                "
                class="multi-inline"
              >
                <label>{{ $t('contractor.labels.effectiveDate') }}</label>
                <FormDatePicker v-model="effectiveDate" @change="addEffectiveDate" />
              </div>
              <div v-if="hasErrorKey('EFFECTIVEDATE')" class="error-div">
                <font-awesome-icon icon="exclamation-circle" />{{ getErrorKey('EFFECTIVEDATE') }}
              </div>
            </div>
            <div
              v-if="isEntitySupplier || formData.entity === 'SUPPLIER' || formData.supplier_name"
              class="form-control"
            >
              <label>{{ $t('contractor.labels.supplier') }}</label>
              <tol-type-ahead
                :search-params="{
                  url: '/contractors/supplier-quick-search',
                  searchKeys: ['name'],
                  objectType: 'supplier'
                }"
                class="blue"
                menu-label="Search Supplier"
                @select-item="onSelectSupplier($event)"
              >
                <template v-slot="{ value }">
                  {{ value.name }}
                </template>
              </tol-type-ahead>
              {{ formData.supplier_name }}
            </div>
          </div>

          <div class="first-last-name-row">
            <div class="form-control">
              <label>{{ $t('contractor.labels.lastName') }}<span class="asterisk">*</span></label>
              <InputField
                v-model="formData.user.last_name"
                type="text"
                class="bordered"
                :class="{ error: formErrors['Last Name'] }"
                @change="onLastNameChange"
              />
            </div>

            <div class="form-control">
              <label>{{ $t('contractor.labels.firstName') }}<span class="asterisk">*</span></label>
              <InputField
                v-model="formData.user.first_name"
                type="text"
                class="bordered"
                :class="{ error: formErrors['First Name'] }"
                @change="onFirstNameChange"
              />
            </div>

            <div class="form-control">
              <label>{{ $t('contractor.labels.preferredName') }}</label>
              <InputField
                v-model="formData.user.preferred_name"
                type="text"
                class="bordered"
                @change="onChange"
              />
            </div>
          </div>
        </div>

        <div class="break" />
        <div class="emails-and-address">
          <div class="emails-and-phone-numbers">
            <div class="form-control">
              <label>{{ $t('contractor.labels.emails') }}<span class="asterisk">*</span></label>
              <InputField
                v-model="formData.user.email"
                type="text"
                class="bordered"
                field-icon="envelope"
                :class="{ error: formErrors['Email'] }"
                @change="onChange"
              />
              <InputField
                v-model="formData.user.email_alt"
                type="text"
                class="bordered"
                field-icon="envelope"
                @change="onChange"
              />
            </div>
            <div class="form-control">
              <label>{{ $t('contractor.labels.phoneNumbers') }}</label>
              <div class="multi-inline">
                <InputField
                  v-model="formData.user.phone1"
                  type="text"
                  class="bordered"
                  field-icon="phone-square"
                  @change="onChange"
                />
                <DropdownSelect
                  :menu-items="phoneTypeOptions"
                  :selected-item="camelCaseOptions(formData.user.phone1_type)"
                  class="blue bordered"
                  @select-item="onPhone1TypeChange"
                />
              </div>
              <div class="multi-inline">
                <InputField
                  v-model="formData.user.phone2"
                  type="text"
                  class="bordered"
                  field-icon="phone-square"
                  @change="onChange"
                />
                <DropdownSelect
                  :menu-items="phoneTypeOptions"
                  :selected-item="camelCaseOptions(formData.user.phone2_type)"
                  class="blue bordered"
                  @select-item="onPhone2TypeChange"
                />
              </div>
              <div class="multi-inline">
                <InputField
                  v-model="formData.user.phone3"
                  type="text"
                  class="bordered"
                  field-icon="phone-square"
                  @change="onChange"
                />
                <DropdownSelect
                  :menu-items="phoneTypeOptions"
                  :selected-item="camelCaseOptions(formData.user.phone3_type)"
                  class="blue bordered"
                  @select-item="onPhone1TypeChange"
                />
              </div>
            </div>
          </div>
          <div class="form-control address">
            <label>{{ $t('contractor.labels.address') }}<span class="asterisk">*</span></label>
            <div>
              <label
                ><strong>{{ $t('contractor.labels.payToName') }}</strong></label
              >
              <InputField
                v-model="formData.pay_to_name"
                :readonly="
                  formData.entity === 'STAFF' ||
                    formData.entity === 'CONSTRUCTION' ||
                    formData.entity === 'SUPPLIER'
                "
                type="text"
                class="bordered"
                :class="{ error: formErrors['Pay To Name'] }"
                @change="onChange"
              />
              <label>{{ $t('contractor.labels.line1') }}</label>
              <InputField
                v-model="formData.address.line1"
                type="text"
                class="bordered"
                :class="{ error: formErrors['Line1'] }"
                @change="onChange"
              />
              <label>{{ $t('contractor.labels.line2') }}</label>
              <InputField
                v-model="formData.address.line2"
                type="text"
                class="bordered"
                @change="onChange"
              />
              <label>{{ $t('contractor.labels.city') }}</label>
              <InputField
                v-model="formData.address.city"
                type="text"
                class="bordered"
                :class="{ error: formErrors['City'] }"
                @change="onChange"
              />
              <label>{{ $t('contractor.labels.province') }}</label>
              <DropdownSelect
                :menu-items="provinceAbbrs"
                :selected-item="formData.address.province_abbr"
                class="blue bordered province-abbrs"
                :class="{ error: formErrors['Province'] }"
                @select-item="onProvinceChange"
              />
              <label>{{ $t('contractor.labels.postalCode') }}</label>
              <InputField
                v-model="formData.address.postal_code"
                type="text"
                class="bordered"
                :class="{ error: formErrors['Postal code'] }"
                @change="onChange"
              />
            </div>
          </div>
        </div>
      </section>

      <section>
        <h1>{{ $t('contractor.labels.governmentNumbers') }}</h1>
        <div class="form-control">
          <label>{{ $t('contractor.labels.sin') }}</label>
          <InputField
            v-model="sin"
            :readonly="!revealSIN"
            type="text"
            class="bordered"
            :class="{ error: formErrors['SIN'] }"
            @click="onToggleReveal('SIN')"
          />
          <div v-if="isTemporarySIN" class="multi-inline">
            <label>{{ $t('contractor.labels.expires') }}</label>
            <FormDatePicker v-model="sinExpiryDate" @change="onSinExpiryDateChange" />
          </div>
        </div>

        <div v-if="displaySSN" class="form-control">
          <label>{{ $t('contractor.labels.ssn') }}</label>
          <InputField
            v-model="ssn"
            :readonly="!revealSSN"
            type="text"
            class="bordered"
            @click="onToggleReveal('SSN')"
          />
        </div>
        <div v-if="displayGST" class="form-control">
          <label>{{ $t('contractor.labels.gst') }}</label>
          <InputField
            v-model="gst"
            :readonly="!revealGST"
            type="text"
            class="bordered"
            @click="onToggleReveal('GST')"
          />
        </div>

        <div v-if="displayQST" class="form-control">
          <label>{{ $t('contractor.labels.qst') }}</label>
          <InputField
            v-model="qst"
            :readonly="!revealQST"
            type="text"
            class="bordered"
            @click="onToggleReveal('QST')"
          />
        </div>

        <div v-if="displayHST" class="form-control">
          <label>{{ $t('contractor.labels.hst') }}</label>
          <InputField
            v-model="hst"
            :readonly="!revealHST"
            type="text"
            class="bordered"
            @click="onToggleReveal('HST')"
          />
        </div>
      </section>

      <section class="supporting-docs-section">
        308
        <h1>{{ $t('contractor.labels.supportingDocuments') }}</h1>

        <div class="supporting-docs-form-container">
          <div class="form-control">
            <label>{{ $t('contractor.labels.aoda') }}</label>
            <div class="multi-inline">
              <Checkbox size="tiny" :checked="formData.is_aoda" @change="onAodaChange" />
              {{ $t('contractor.labels.signedFormOnFile') }}
            </div>
            <div class="multi-inline nowrap">
              <label>{{ $t('contractor.labels.orCode') }}</label>
              <InputField
                v-model="formData.aoda_code"
                type="text"
                class="bordered"
                @change="onChange"
              />
            </div>
          </div>

          <div class="form-control">
            <label>{{ $t('contractor.labels.backcheck') }}</label>
            <FormDatePicker v-model="backcheckDate" @change="onBackcheckDateChange" />
          </div>

          <div class="form-control">
            <label>{{ $t('contractor.labels.whmis') }}</label>
            <FormDatePicker v-model="whmis" @change="onWhmisChange" />
          </div>

          <div class="form-control">
            <label>{{ $t('contractor.labels.whmisGhs') }}</label>
            <FormDatePicker v-model="whmisGhs" @change="onWhmisGhsChange" />
          </div>

          <div class="form-control">
            <label>{{ $t('contractor.labels.healthAndSafety') }}</label>
            <FormDatePicker v-model="healthAndSafetyDate" @change="onHealthAndSafetyDateChange" />
          </div>

          <div class="form-control">
            <label>{{ $t('contractor.labels.workplaceViolence') }}</label>
            <FormDatePicker
              v-model="workplaceViolenceDate"
              @change="onWorkplaceViolenceDateChange"
            />
          </div>
        </div>
      </section>
      <section class="misc-section">
        353
        <h1>{{ $t('contractor.labels.miscDetails') }}</h1>
        <div class="misc-section-container">
          <div class="misc-radio-buttons">
            <div class="form-control">
              <label>{{ $t('contractor.labels.cpp') }}</label>
              <TolRadioButton
                :button-label="$t('contractor.labels.receiving')"
                label="true"
                name="cpp"
                :value="formData.is_cpp"
                @toggle="formData.is_cpp = true"
                @change="onChange"
              />
              <TolRadioButton
                :button-label="$t('contractor.labels.contributing')"
                label="false"
                name="cpp"
                :value="formData.is_cpp"
                @toggle="formData.is_cpp = false"
                @change="onChange"
              />
            </div>

            <div class="form-control">
              <label>{{ $t('contractor.labels.active') }}</label>
              <TolRadioButton
                button-label="Yes"
                label="true"
                name="active"
                :value="formData.user.is_active"
                @toggle="formData.user.is_active = true"
                @change="onChange"
              />
              <TolRadioButton
                button-label="No"
                label="false"
                name="active"
                :value="formData.user.is_active"
                @toggle="formData.user.is_active = false"
                @change="onChange"
              />
            </div>

            <div class="form-control">
              <label>{{ $t('contractor.labels.postalCodeVerified') }}</label>
              <TolRadioButton
                button-label="Yes"
                label="true"
                name="verified"
                :value="formData.is_verified"
                @toggle="formData.is_verified = true"
                @change="onChange"
              />
              <TolRadioButton
                button-label="No"
                label="false"
                name="verified"
                :value="formData.is_verified"
                @toggle="formData.is_verified = false"
              />
            </div>
          </div>

          <div class="source-and-vaccination-row">
            <div class="form-control">
              <label>{{ $t('contractor.labels.source') }}<span class="asterisk">*</span></label>
              <DropdownSelect
                :menu-items="sources"
                :selected-item="formData.source"
                class="blue constant bordered"
                :value="formData.source"
                :class="{ error: formErrors['Source'] }"
                @select-item="onSourceChange($event)"
              />
              <br />
              <label>{{ $t('contractor.labels.sourceNote') }}<span class="asterisk">*</span></label>
              <InputField
                v-model="formData.source_note"
                type="text"
                class="bordered"
                :class="{ error: formErrors['Source Note'] }"
                @change="onChange"
              />
            </div>

            <div class="form-control">
              <label>{{ $t('contractor.labels.vaccinationStatus') }}</label>
              <DropdownSelect
                :menu-items="vaccinationChoices"
                :selected-item="formData.vaccination_status"
                class="blue bordered"
                :value="formData.vaccination_status"
                @select-item="onVaccinationChange($event)"
              />
              <br />
            </div>
          </div>

          <div class="form-control">
            <label>{{ $t('contractor.labels.vaccinationDetails') }}</label>
            <textarea
              v-model="formData.vaccination_details"
              rows="3"
              cols="30"
              @change="onChange"
            />
          </div>

          <div class="form-control">
            <label>{{ $t('contractor.labels.comments') }}</label>
            <textarea v-model="formData.general_comments" rows="3" cols="30" @change="onChange" />
          </div>

          <div class="form-control">
            <label>{{ $t('contractor.labels.externalNotes') }}</label>
            <textarea v-model="formData.external_notes" rows="3" cols="30" @change="onChange" />
          </div>
        </div>
      </section>
      <div v-if="!isHistoryRecord" class="module-action-container">
        <tol-button
          id="save-btn"
          button-type="save"
          label="Save"
          :class="[
            'module-action',
            { disabled: !isOnScreenDataModified && formData.reference_number }
          ]"
          @click="doContractorSave(formData.reference_number ? 'SAVE' : 'CREATE')"
        >
        </tol-button>
      </div>
    </form>
    <update-modal v-if="isProcessFormBtnClicked">
      <template v-slot:modalData>
        <vue-simple-spinner size="massive" />
      </template>
    </update-modal>
  </div>
</template>

<script>
import DropdownSelect from '@/app/components/DropdownSelect';
import InputField from '@/app/components/InputField';
import { AppGetters } from '@/app/app.keys';
import TolRadioButton from '@/app/components/TolRadioButton';
import Checkbox from '@/app/components/Checkbox';
import UpdateModal from '@/app/components/UpdateModal';
import TolButton from '@/app/components/TolButton';
import format from 'date-fns/format';
import FormDatePicker from '@/app/components/FormDatePicker';
import { clone } from 'ramda';
import { mask } from '@/mixins/mask';
import { dateUtils } from '@/mixins/date-utils';
import { ContractorDetailsActions } from '../contractor-details.keys';
import { TolErrorsGetters } from '../../../../tol-errors/tol-errors.keys';
import { disableForm } from '@/mixins/disable-form';
import TolList from '@/app/components/TolList';

import TolTypeAhead from '@/app/components/TolTypeAhead';
import { ContractorDetailsGetters } from '../contractor-details.keys';
import camelCase from '../../../../../../filters/camel-case.filter';
export default {
  name: 'ContractorDetailsForm',
  components: {
    DropdownSelect,
    InputField,
    TolRadioButton,
    FormDatePicker,
    Checkbox,
    TolButton,
    UpdateModal,
    TolList,
    TolTypeAhead
  },
  mixins: [mask, dateUtils, disableForm],
  props: {
    contractor: {
      type: Object,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    isHistoryRecord: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formData: {},
      revealHST: false,
      revealQST: false,
      revealGST: false,
      revealSSN: false,
      revealSIN: false,
      isOnScreenDataModified: false,
      isProcessFormBtnClicked: false,
      areFormFieldsDisabled: false,
      isEntitySupplier: false,
      isEffectiveDateEnabled: false,
      clientValidation: [],
      currentPayroll: null
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    phoneTypeOptions() {
      let phoneTypes = this.$store.getters[AppGetters.PhoneTypes].map(
        type => type[0].toUpperCase() + type.substring(1).toLowerCase()
      );
      return phoneTypes;
    },
    provinceAbbrs() {
      return this.$store.getters[AppGetters.ProvinceAbbreviations];
    },
    entityTypes() {
      let entityTypes = this.$store.getters[AppGetters.EntityTypes].map(
        type => type[0].toUpperCase() + type.substring(1).toLowerCase()
      );
      return entityTypes;
    },
    sin: {
      get() {
        if (this.revealSIN) return this.formData.sin;
        else return this.maskSINOrSSN(this.formData.sin);
      },
      set(value) {
        this.formData.sin = value;
        this.isOnScreenDataModified = true;
      }
    },
    sinExpiryDate() {
      return this.formData.sin_expiry ? this.dateFromString(this.formData.sin_expiry) : null;
    },
    effectiveDate() {
      return this.formData.effective_date
        ? this.dateFromString(this.formData.effective_date)
        : null;
    },
    backcheckDate() {
      return this.formData.backcheck_date
        ? this.dateFromString(this.formData.backcheck_date)
        : null;
    },
    whmis() {
      return this.formData.whmis ? this.dateFromString(this.formData.whmis) : null;
    },
    whmisGhs() {
      return this.formData.whmis_ghs ? this.dateFromString(this.formData.whmis_ghs) : null;
    },
    healthAndSafetyDate() {
      return this.formData.health_and_safety_date
        ? this.dateFromString(this.formData.health_and_safety_date)
        : null;
    },
    workplaceViolenceDate() {
      return this.formData.workplace_violence_date
        ? this.dateFromString(this.formData.workplace_violence_date)
        : null;
    },
    isTemporarySIN() {
      return this.formData.sin.indexOf('9') === 0;
    },
    canViewUserAccount() {
      return (
        !!this.currentUser &&
        !!this.currentUser.permissions['USER_ACCOUNT'] &&
        !!this.currentUser.permissions['USER_ACCOUNT']['VIEW']
      );
    },
    ssn: {
      get() {
        return this.revealSSN ? this.formData.ssn : this.maskSINOrSSN(this.formData.ssn);
      },
      set(value) {
        this.formData.ssn = value;
        this.isOnScreenDataModified = true;
      }
    },
    gst: {
      get() {
        return this.revealGST ? this.formData.gst : this.maskHSTOrGSTOrQST(this.formData.gst);
      },
      set(value) {
        this.formData.gst = value;
        this.isOnScreenDataModified = true;
      }
    },
    qst: {
      get() {
        return this.revealQST ? this.formData.qst : this.maskHSTOrGSTOrQST(this.formData.qst);
      },

      set(value) {
        this.formData.qst = value;
        this.isOnScreenDataModified = true;
      }
    },
    hst: {
      get() {
        return this.revealHST ? this.formData.hst : this.maskHSTOrGSTOrQST(this.formData.hst);
      },

      set(value) {
        this.formData.hst = value;
        this.isOnScreenDataModified = true;
      }
    },
    sources() {
      return this.$store.getters[AppGetters.ContractorSources];
    },
    vaccinationChoices() {
      return ['Unvaccinated', 'Partially Vaccinated', 'Vaccinated', 'Exempt', 'Other'];
    },
    currentSaveStatus() {
      return this.$store.getters[TolErrorsGetters.HasErrors] ? 'error' : 'success';
    },
    displaySSN() {
      return (
        this.formData.entity === 'STAFF' ||
        this.formData.entity === 'CONSTRUCTION' ||
        this.formData.entity === ''
      );
    },
    displayHST() {
      return (
        this.formData.entity !== 'STAFF' &&
        this.formData.entity !== 'CONSTRUCTION' &&
        this.formData.entity !== 'SUPPLIER'
      );
    },
    displayQST() {
      return this.formData.entity === 'PRT' || this.formData.entity === 'INC';
    },
    displayGST() {
      return (
        this.formData.entity !== 'STAFF' &&
        this.formData.entity !== 'CONSTRUCTION' &&
        this.formData.entity !== 'SUPPLIER'
      );
    },
    formErrors() {
      return this.$store.getters[ContractorDetailsGetters.ContractorValidationErrors];
    }
  },
  watch: {
    contractor: {
      immediate: true,
      handler() {
        this.formData = clone(this.contractor);
        if (!this.formData.address) {
          this.formData.address = {
            line1: null,
            line2: null,
            city: null,
            postal_code: null,
            province_abbr: null
          };
        }
        if (this.formData.entity === 'SUPPLIER' && !this.formData.pay_to_name) {
          this.formData.pay_to_name = this.formData.supplier_name;
        }
      }
    }
  },
  updated() {
    if (this.isHistoryRecord && !this.areFormFieldsDisabled) {
      const forms = document.querySelectorAll('.contractor-form.history form');
      forms.forEach(form => this.disableFormElements(form));
      this.areFormFieldsDisabled = true;
    }
  },

  methods: {
    camelCaseOptions(string) {
      return camelCase(string);
    },
    formatDate(d) {
      return d ? format(d, 'YYYY-MM-DD') : null;
    },
    changePayToName() {
      if (this.formData.entity === 'STAFF' || this.formData.entity === 'CONSTRUCTION') {
        this.formData.pay_to_name =
          this.formData.user.first_name + ' ' + this.formData.user.last_name;
      }
    },
    onFirstNameChange() {
      this.isOnScreenDataModified = true;
      this.changePayToName();
    },
    onLastNameChange() {
      this.isOnScreenDataModified = true;
      this.changePayToName();
    },
    addPayrollNum(payroll) {
      if (payroll != null) {
        this.formData.payroll_nums.push(payroll);
      }
      this.currentPayroll = null;
      this.isOnScreenDataModified = true;
    },
    removePayrollNum(payroll) {
      this.formData.payroll_nums = this.formData.payroll_nums.filter(num => num !== payroll);
      this.isOnScreenDataModified = true;
    },
    onChange() {
      this.isOnScreenDataModified = true;
    },
    onEntityTypeChange(event) {
      this.formData.entity = event.toUpperCase();
      if (this.formData.entity !== 'SUPPLIER') {
        this.formData.supplier_name = null;
        this.formData.supplier_id = null;
        this.isEntitySupplier = false;
      }
      this.isEffectiveDateEnabled = true;
      if (this.formData.entity === 'SUPPLIER') this.isEntitySupplier = true;
      this.changePayToName();
      this.isOnScreenDataModified = true;
    },
    addEffectiveDate(event) {
      if (this.isHistoryRecord) {
        return;
      }

      let isDateValid = true;
      const assignments = this.$store.getters[
        ContractorDetailsGetters.CurrentContractorAssignments
      ];

      assignments.map(assignment => {
        if (assignment.stopDate === null || this.formatDate(event) < assignment.stopDate) {
          this.clientValidation.push({
            field: 'EFFECTIVEDATE',
            message: this.$t('contractor.labels.invalidEffectiveDate')
          });
          isDateValid = false;
        }
      });

      if (isDateValid) {
        this.formData.effective_date = this.formatDate(event);
        this.isOnScreenDataModified = true;
        this.clearClientValidation();
      }
    },
    hasErrorKey(key) {
      return this.clientValidation.filter(v => v.field === key).length;
    },
    getErrorKey(key) {
      return this.clientValidation.filter(v => v.field === key)[0].message;
    },
    clearClientValidation() {
      this.clientValidation = [];
    },
    onPhone1TypeChange(event) {
      this.formData.user.phone1_type = event.toUpperCase();
      this.isOnScreenDataModified = true;
    },
    onPhone2TypeChange(event) {
      this.formData.user.phone2_type = event.toUpperCase();
      this.isOnScreenDataModified = true;
    },
    onPhone3TypeChange(event) {
      this.formData.user.phone3_type = event.toUpperCase();
      this.isOnScreenDataModified = true;
    },
    onSinExpiryDateChange(event) {
      if (this.isHistoryRecord) {
        return;
      }
      this.formData.sin_expiry = this.formatDate(event);
      this.isOnScreenDataModified = true;
    },
    onBackcheckDateChange(event) {
      if (this.isHistoryRecord) {
        return;
      }
      this.formData.backcheck_date = this.formatDate(event);
      this.isOnScreenDataModified = true;
    },
    onWhmisChange(event) {
      if (this.isHistoryRecord) {
        return;
      }
      this.formData.whmis = this.formatDate(event);
      this.isOnScreenDataModified = true;
    },
    onWhmisGhsChange(event) {
      if (this.isHistoryRecord) {
        return;
      }
      this.formData.whmis_ghs = this.formatDate(event);
      this.isOnScreenDataModified = true;
    },
    onWorkplaceViolenceDateChange(event) {
      if (this.isHistoryRecord) {
        return;
      }
      this.formData.workplace_violence_date = this.formatDate(event);
      this.isOnScreenDataModified = true;
    },
    onHealthAndSafetyDateChange(event) {
      if (this.isHistoryRecord) {
        return;
      }
      this.formData.health_and_safety_date = this.formatDate(event);
      this.isOnScreenDataModified = true;
    },
    onProvinceChange(event) {
      this.formData.address.province_abbr = event;
      this.isOnScreenDataModified = true;
    },
    onSourceChange(event) {
      this.formData.source = event;
      this.isOnScreenDataModified = true;
    },
    onVaccinationChange(event) {
      this.formData.vaccination_status = event;
      this.isOnScreenDataModifited = true;
    },
    onAodaChange() {
      this.formData.is_aoda = !this.formData.is_aoda;
      this.isOnScreenDataModified = true;
    },
    onToggleReveal(revealEntity) {
      switch (revealEntity) {
        case 'HST':
          this.revealHST = !this.revealHST;
          break;
        case 'QST':
          this.revealQST = !this.revealQST;
          break;
        case 'GST':
          this.revealGST = !this.revealGST;
          break;
        case 'SSN':
          this.revealSSN = !this.revealSSN;
          break;
        case 'SIN':
          this.revealSIN = !this.revealSIN;
          break;
      }
    },
    onSelectSupplier(event) {
      this.isOnScreenDataModified = true;
      this.formData.supplier_name = event.name;
      this.formData.supplier_id = event.supplier_id;
      this.formData.pay_to_name = this.formData.supplier_name;
      this.formData.address = {
        id: event.id,
        line1: event.line1,
        line2: event.line2,
        city: event.city,
        province_abbr: event.province_abbr,
        postal_code: event.postal_code
      };
    },

    resetEntities() {
      this.revealHST = false;
      this.revealQST = false;
      this.revealGST = false;
      this.revealSSN = false;
      this.revealSIN = false;
    },
    doContractorSave(action) {
      this.resetEntities();
      if (!this.isOnScreenDataModified) {
        return;
      }
      if (!this.isProcessFormBtnClicked) {
        this.$store.dispatch(ContractorDetailsActions.SaveContractor, {
          contractorData: this.formData,
          action
        });
      }
      this.isProcessFormBtnClicked = true;
      window.setTimeout(() => {
        this.isProcessFormBtnClicked = false;
        this.isOnScreenDataModified = false;
      }, 2000);
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
@import '@/app/styles/action-buttons.scss';

.supporting-docs-form-container {
  width: 50%;
  display: flex;
  flex-wrap: wrap;
}

.supporting-docs-form-container > div {
  flex: 1 1 7vw !important;
}

.misc-section-container > div {
  width: 30%;
  width: 70%;
  display: flex;
  flex-direction: column;

  & textarea {
    width: 100%;
  }
}

.misc-section-container {
  width: 70%;
}

.misc-section-container .source-and-vaccination-row {
  flex-direction: row;
  width: 80%;
}

.misc-section-container .misc-radio-buttons {
  width: 50vw;
  display: flex;
  flex-direction: row;
  justify-content: left !important;

  & div {
    display: flex;
    flex-direction: row !important;
    margin-right: 40px !important;
  }

  & div :first-child {
    font-weight: bold !important;
  }

  & div > * {
    margin: 0px 10px 0px 10px !important;
    width: fit-content;
    font-weight: normal !important;
  }
}

.first-last-name-row {
  display: flex;
}
.entity-and-payroll-row {
  display: flex;
}
.emails-and-address {
  display: flex;
  flex-direction: row;
}
.emails-and-phone-numbers {
  flex-grow: 0.35;
  div .input-container {
    margin: 3px 0 3px 0;
  }
}
.contractor-form {
  &.history {
    font-size: 0.8em;
    background-color: $light-grey-2;
    padding: 25px;
  }
  form {
    width: 100%;
    section {
      margin-bottom: 20px;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      background-color: $background-level-3;

      h1 {
        padding-top: 15px;
        border-bottom: 1px solid $row-border;
      }
      h1,
      h2 {
        flex: 0 100%;
      }

      a {
        text-decoration: none;
      }

      .break {
        width: 100%;
        height: 0;
      }

      .inactive {
        color: $red;
        border: 1px solid $red;
        padding: 0 5px;
        margin-left: 5px;
      }

      .form-control {
        padding: 10px 0;
        margin: 1em;
        flex: 1;
        > label {
          display: block;
          font-weight: bold;
        }

        .multi-inline {
          display: flex;
          justify-content: flex-start;
          align-items: flex-end;

          > * {
            margin-left: 1em;
          }
          > *:first-child {
            margin-left: 0;
          }
          .dropdown-container {
            width: 30%;
          }
        }
        .error,
        .error-div {
          color: $red;
          background-color: $modified-form;
        }
        .error-div {
          padding: 8px;
          &:hover {
            cursor: pointer;
          }
          svg {
            margin-right: 15px;
          }
        }

        &.address {
          width: 45vw;

          > label {
            width: 50%;
            margin-left: 50%;
          }

          > div {
            display: flex;
            flex-wrap: wrap;
            align-items: flex-end;

            > label {
              text-align: right;
            }

            > * {
              flex: 50%;
              padding-right: 1em;
            }

            .province-abbrs {
              width: 4em;
            }
          }
        }
      }
    }

    .form-input {
      border-bottom: 1px solid $blue;
      font-size: 1em;
      height: inherit;
      &:focus {
        outline: none;
      }

      &.small {
        width: auto;
        display: inline-flex;
        margin-right: 15px;
      }
    }

    .module-action-container {
      justify-content: flex-end;
    }
  }

  .list-header {
    display: flex;
    justify-content: space-between;
    border-bottom: 4px solid $row-border;
    font-weight: bold;
    span {
      display: inline-block;
      font-size: 1.2em;
      font-weight: bold;
      width: 20px;
      height: 20px;
      line-height: 20px;
      border-radius: 100%;
      text-align: center;
      &:hover {
        cursor: pointer;
      }
      &.history {
        display: none;
      }
    }
  }

  .nowrap {
    white-space: nowrap;
  }
}
.asterisk {
  color: red;
}
</style>
